import { useEffect, useMemo } from "react";

import {
  useLazyGetOrganizationByIdQuery,
  useLazyGetSectionsQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import type { SectionResponse } from "@/redux/survey/types";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";
import SurveyViewDetails from "./components/SurveyViewDetails";
import SurveyOverviewHeader from "./components/SurveyOverviewHeader";
import type { SurveyOverviewProps } from "./types";

import "./styles.css";

const SurveyOverview = ({ survey, organizationId }: SurveyOverviewProps) => {
  const currentOrganizationId = useOrganizationId();
  const [getSections, sectionsResponse] = useLazyGetSectionsQuery();
  const [getSurveyStatics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [getOrganizationById, organizationResponse] =
    useLazyGetOrganizationByIdQuery();

  useEffect(() => {
    getSections({
      ids: [],
      survey: survey.id,
      parentOnly: true,
      pagination: "off",
    });
    getSurveyStatics({
      survey: survey.id,
      organization: organizationId,
    });
    if (organizationId) {
      getOrganizationById(organizationId);
    }
  }, [survey.id, organizationId]);

  const organizationName = useMemo(() => {
    if (organizationResponse.isSuccess) {
      return organizationResponse.data.name;
    }
    return null;
  }, [organizationResponse]);

  const sections = sectionsResponse.isSuccess
    ? (sectionsResponse.data as SectionResponse[])
    : null;

  return (
    <div className="survey-overview-page flex flex-col full-width">
      <SurveyOverviewHeader
        survey={survey}
        organizationName={organizationName}
        statistics={
          surveyStatisticsResponse.isSuccess
            ? surveyStatisticsResponse.data
            : undefined
        }
        isStatisticsLoading={surveyStatisticsResponse.isLoading}
        showGenerateViews={
          !!organizationResponse.data &&
          currentOrganizationId === organizationResponse.data.id
        }
      />

      {sections ? (
        <div className="survey-overview-body flex flex-col gap-300">
          <SurveyViewDetails
            sections={sections}
            surveyStatisticsResponse={surveyStatisticsResponse}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SurveyOverview;
